import tlds from 'tlds';
import { object, ref, string } from 'yup';
import { TFunction } from 'i18next';
import { MIN_PASSWORD_LENGTH } from '~/Constants/Values.constants';

export const REQUIRED = 'Required';
export const INVALID_EMAIL_ADDRESS = 'Invalid email address';

export const validateEmail = (value: string | undefined | null) => {
  if (!value) {
    return REQUIRED;
  } else if (
    // eslint-disable-next-line no-useless-escape
    !/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
      value,
    )
  ) {
    return INVALID_EMAIL_ADDRESS;
  }
};

export const passwordValidationScheme = (t: TFunction) =>
  object({
    password: string()
      .required(t('userService.passwordIsRequired'))
      .matches(/[0-9]/, t('onboarding.error.password.oneLetter'))
      .matches(/[!@#$%^&*]/, t('onboarding.error.password.specialLetter'))
      .matches(/[A-Z]/, t('onboarding.error.password.UpperCaseLetter'))
      .matches(
        new RegExp(`(?=.{${MIN_PASSWORD_LENGTH},})`),
        t('onboarding.error.password.length', { length: MIN_PASSWORD_LENGTH }),
      ),
    password_confirmation: string().oneOf(
      [ref('password'), null],
      t('userService.passwordConfirmNotMatch'),
    ),
  });

export const changePasswordValidationScheme = (t: TFunction) =>
  object({
    old_password: string().required(t('app.form.validation.required')),
    password: string()
      .required(t('userService.passwordIsRequired'))
      .matches(/[0-9]/, t('onboarding.error.password.oneLetter'))
      .matches(/[!@#$%^&*]/, t('onboarding.error.password.specialLetter'))
      .matches(/[A-Z]/, t('onboarding.error.password.UpperCaseLetter'))
      .matches(
        new RegExp(`(?=.{${MIN_PASSWORD_LENGTH},})`),
        t('onboarding.error.password.length', { length: MIN_PASSWORD_LENGTH }),
      ),
    password_confirmation: string()
      .required(t('app.form.validation.required'))
      .oneOf([ref('password'), null], t('userService.passwordConfirmNotMatch')),
  });

export const customerValidationScheme = object({
  firstname: string()
    .required('Firstname is required')
    .matches(/(?=.*[A-Za-z\säüö])/g, 'Firstname can only contain letters'),
  lastname: string()
    .required('Lastname is required')
    .matches(/(?=.*[A-Za-z\säöü-])/g, 'Lastname can only contain letters'),
  email: string()
    .min(1)
    .matches(/^[^@]+@[^@]+\.[^@]+$/g, 'Email address needs to be valid'),
  phone: string(),
});

export const userProfileValidationScheme = object({
  first_name: string().matches(/(?=.*[A-Za-z\säüö])/g, 'Firstname can only contain letters'),
  last_name: string().matches(/(?=.*[A-Za-z\säöü-])/g, 'Lastname can only contain letters'),
  email: string()
    .min(1)
    .required('Email is required')
    .matches(/^[^@]+@[^@]+\.[^@]+$/g, 'Email address needs to be valid'),
});

export const weblinkValidationScheme = (options: { exact: boolean; strict: boolean }) => {
  const protocol = `(?:(?:[a-z]+:)?//)${options.strict ? '' : '?'}`;
  const auth = '(?:\\S+(?::\\S*)?@)?';
  const ip =
    '(?:25[0-5]|2[0-4]\\d|1\\d\\d|[1-9]\\d|\\d)(?:\\.(?:25[0-5]|2[0-4]\\d|1\\d\\d|[1-9]\\d|\\d)){3}';
  const host = '(?:(?:[a-z\\u00a1-\\uffff0-9][-_]*)*[a-z\\u00a1-\\uffff0-9]+)';
  const domain = '(?:\\.(?:[a-z\\u00a1-\\uffff0-9]-*)*[a-z\\u00a1-\\uffff0-9]+)*';
  const tld = `(?:\\.${
    options.strict
      ? '(?:[a-z\\u00a1-\\uffff]{2,})'
      : `(?:${tlds.sort((a, b) => b.length - a.length).join('|')})`
  })\\.?`;
  const port = '(?::\\d{2,5})?';
  const path = '(?:[/?#][^\\s"]*)?';
  const regex = `(?:${protocol}|www\\.)${auth}(?:localhost|${ip}|${host}${domain}${tld})${port}${path}`;

  return options.exact ? new RegExp(`(?:^${regex}$)`, 'i') : new RegExp(regex, 'ig');
};
